import { Link, NavLink } from 'react-router-dom';
import { X } from '@phosphor-icons/react';
import BrandLogo, { useBrand } from '../Brand';
import SocialIcon from '../SocialIcon';
import { useSettings } from '../../context/SettingsContext';
import { activeSocials, socialLink, SOCIAL_KINDS } from '../../lib/socials';

const navItems = [
  { path: '/', label: 'Ana Sayfa', img: '/icons3d/home.png' },
  { path: '/sponsorlar', label: 'Sponsorlar', img: '/icons3d/gem.png' },
  { path: '/gorevler', label: 'Görevler', img: '/icons3d/target.png', mk: 'tasks', badge: 'Günlük', badgeTone: 'emerald' },
  { path: '/market', label: 'Puan Marketi', img: '/icons3d/market.png', mk: 'market' },
  { path: '/oyunlar', label: 'Oyunlar', img: '/icons3d/slot.png', mk: 'games' },
  { path: '/turnuvalar', label: 'Turnuvalar', img: '/icons3d/trophy.png', mk: 'tournaments', badge: 'Yeni', badgeTone: 'accent' },
  { path: '/liderlik', label: 'Liderlik', img: '/icons3d/rank-gold.png', mk: 'leaderboard' },
  { path: '/cark', label: 'Şans Çarkı', img: '/icons3d/wheel.png', mk: 'wheel', badge: 'Ücretsiz', badgeTone: 'gold' },
  { path: '/promokod', label: 'Promo Kod', img: '/icons3d/ticket.png?v=3', mk: 'promo' },
  { path: '/haberler', label: 'Haberler', img: '/icons3d/megaphone.png', mk: 'news' },
];

const SidebarContent = ({ onClose }) => {
  const { name } = useBrand();
  const { site } = useSettings();
  const socials = activeSocials(site);
  const telegram = socials.find((s) => s.kind === 'telegram');
  return (
  <div className="flex flex-col h-full">
    <div className="h-20 flex items-center gap-2 px-3 sm:px-4 border-b border-white/5 shrink-0">
      <Link to="/" data-testid="sidebar-logo-link" className="flex items-center flex-1 min-w-0" onClick={onClose}>
        <div className="neon-always rounded-xl border border-white/10 bg-bh-canvas px-3 sm:px-4 py-2.5 flex items-center justify-center w-full overflow-hidden">
          <BrandLogo className="h-9 sm:h-10 w-auto max-w-full object-contain drop-shadow-[0_3px_10px_rgba(47,180,249,0.4)]" />
        </div>
      </Link>
      <button onClick={onClose} className="lg:hidden w-11 h-11 rounded-lg btn-ghost flex items-center justify-center shrink-0" data-testid="sidebar-close-btn">
        <X className="w-4 h-4" />
      </button>
    </div>

    <div className="p-3 grid grid-cols-2 gap-2 shrink-0">
      <Link to="/gorevler" onClick={onClose} data-testid="sidebar-quick-points" className="panel panel-hover p-3 flex flex-col items-center gap-1.5 !rounded-xl">
        <img src="/icons3d/coins.png" alt="" className="w-8 h-8 object-contain" />
        <span className="text-[11px] font-medium text-gray-300">Puan Al</span>
      </Link>
      <Link to="/promokod" onClick={onClose} data-testid="sidebar-quick-promo" className="panel panel-hover p-3 flex flex-col items-center gap-1.5 !rounded-xl">
        <img src="/icons3d/ticket.png?v=3" alt="" className="w-8 h-8 object-contain" />
        <span className="text-[11px] font-medium text-gray-300">Promo Kod</span>
      </Link>
    </div>

    <nav className="flex-1 overflow-y-auto no-scrollbar px-3 pb-3 space-y-0.5">
      {navItems.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.path === '/'}
          onClick={onClose}
          data-testid={`sidebar-link-${item.path === '/' ? 'anasayfa' : item.path.slice(1)}`}
          className={({ isActive }) => `side-item ${isActive ? 'active' : ''}`}
        >
          <img src={item.img} alt="" className="w-6 h-6 object-contain shrink-0 drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]" />
          <span className="flex-1">{item.label}</span>
          {item.badge && <span className={`nav-badge nav-badge-${item.badgeTone}`}>{item.badge}</span>}
        </NavLink>
      ))}
    </nav>

    <div className="p-3 shrink-0 space-y-2">
      {socials.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {socials.map((s, i) => (
            <a
              key={`${s.kind}-${i}`}
              href={socialLink(s)}
              target="_blank"
              rel="noopener noreferrer"
              title={s.label || SOCIAL_KINDS.find((k) => k.id === s.kind)?.label || s.kind}
              className="w-9 h-9 rounded-lg btn-ghost flex items-center justify-center text-gray-400 hover:text-bh-accent"
            >
              <SocialIcon kind={s.kind} />
            </a>
          ))}
        </div>
      )}
      <a
        href={telegram ? socialLink(telegram) : '#!'}
        target={telegram ? '_blank' : undefined}
        rel={telegram ? 'noopener noreferrer' : undefined}
        onClick={!telegram ? (e) => e.preventDefault() : undefined}
        data-testid="sidebar-telegram-card"
        className="tg-cta block"
      >
        <span className="tg-cta-glow" aria-hidden />
        <span className="relative z-[1] flex items-center gap-3">
          <img src="/icons3d/telegram.png" alt="" className="tg-cta-ico" />
          <span className="min-w-0">
            <span className="tg-cta-title">{telegram?.label || `${name} Telegram`}</span>
            <span className="tg-cta-sub">Günlük puan kodları</span>
          </span>
        </span>
      </a>
    </div>
  </div>
  );
};

const Sidebar = ({ open, onClose }) => (
  <>
    <aside className="hidden lg:block w-60 shrink-0 border-r border-white/5 bg-bh-canvas">
      <div className="sticky top-0 h-screen">
        <SidebarContent onClose={() => {}} />
      </div>
    </aside>

    {open && (
      <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-sidebar-overlay">
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
        <aside className="absolute inset-y-0 left-0 w-[min(18rem,100%)] bg-bh-canvas border-r border-white/10 shadow-2xl pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
          <SidebarContent onClose={onClose} />
        </aside>
      </div>
    )}
  </>
);

export default Sidebar;
